"use client";

import { PORTALS } from ".constants/portals";
import { Icon, usePortalContext } from "@cloai/uikit";
import { useLocale, useTranslations } from "next-intl";
import { FC, SyntheticEvent, useCallback, useMemo } from "react";

import { InputUndressType } from "@/.gql/graphql";
import { ResetReUseFacePortal } from "@/portals";
import {
  useAiRequestsContext,
  useAutoMaskContext,
  useUndressContext,
} from "@/providers";
import PromptImg from "@/styles/image/prompt.webp";
import { cn } from "@/utils/cn";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { DetailsCard } from "./DetailsCard";
import { STYLES } from "./styles";
import { useCardData } from "./useCardData";

export const UndressDetailsPlates: FC = () => {
  const t = useTranslations("HomePage.detailsCard");
  const {
    clickFileId,
    clickUndressStatus,
    clickSettings,
    clickUndressingFile,
    clickUndress,
    selectedCollectionCount: selectedSetCount,
    reUseFaceObj,
    updatePhotoSetting,
    initialSettings,
  } = useUndressContext(); // Контекст с данными о "раздевании" (`undress`)
  const { costumes, poses, sets, storylines, clips } = useAiRequestsContext();
  const { isShowDraw } = useAutoMaskContext(); // Контекст для рисования маски
  const { openPortal } = usePortalContext(); // Контекст попапов

  const locale = useLocale();

  const { isEndAllUndress, isInit, isError, isBlur } = clickUndressStatus;
  const { costume, pose, set, storyline, clip } = clickSettings;

  const undressIds = useMemo(
    () => clickUndress?.aiRequestIds ?? [],
    [clickUndress?.aiRequestIds],
  );

  const costumeData = useCardData(costumes, costume, undressIds);
  const poseData = useCardData(poses, pose, undressIds);
  const setData = useCardData(sets, set, undressIds);
  const storylineData = useCardData(storylines, storyline, undressIds);
  const clipData = useCardData(clips, clip, undressIds);

  const collectionData = setData || storylineData;

  const undressType = useMemo(() => {
    const isReuseMode = !!(reUseFaceObj && !clickUndressingFile);
    const isAnimationMode =
      clickUndress?.undressType === InputUndressType.Aniamtion;
    const isCollection = !!collectionData;
    const isClip = !!clipData;
    const isPrompt =
      !costumeData && !isCollection && !clipData && clickUndressingFile;
    const isClassicMode =
      !isReuseMode && !isAnimationMode && !collectionData && !isClip;

    return {
      isReuseMode,
      isAnimationMode,
      isCollection,
      isClip,
      isPrompt,
      isClassicMode,
    };
  }, [
    reUseFaceObj,
    clickUndressingFile,
    clickUndress?.undressType,
    collectionData,
    clipData,
    costumeData,
  ]);

  const cardData = useMemo(() => {
    if (reUseFaceObj && !clickUndressingFile) {
      return {
        title: t("title.mode"),
        data: {
          image: reUseFaceObj?.file.fileUrl || PromptImg.src,
          name: { [locale]: t("mod.reuse") },
          collection: { [locale]: t("mod.reuse") },
        },
      };
    }

    if (clickUndress?.undressType === InputUndressType.Aniamtion) {
      return {
        title: t("title.mode"),
        data: {
          image: clickUndress?.fileUrl,
          name: { [locale]: t("mod.animation") },
          collection: { [locale]: t("mod.animation") },
        },
      };
    }

    if (clipData) {
      return { title: t("title.video"), data: clipData };
    }

    if (setData) {
      return { title: t("title.set"), data: setData };
    }

    if (storylineData) {
      return { title: t("title.storyline"), data: storylineData };
    }

    if (costumeData) {
      return { title: t("title.costume"), data: costumeData };
    }

    return {
      title: t("title.mode"),
      data: {
        image: PromptImg.src,
        name: { [locale]: t("mod.prompt") },
        collection: { [locale]: t("mod.prompt") },
      },
    };
  }, [
    clickUndress?.fileUrl,
    clickUndress?.undressType,
    clickUndressingFile,
    clipData,
    costumeData,
    locale,
    reUseFaceObj,
    setData,
    storylineData,
    t,
  ]);

  const handleEditCard = useCallback(() => {
    if (!isInit) {
      return;
    }

    if (undressType.isPrompt) {
      return openPortal(PORTALS.USER_FLOW_PROMPT);
    }

    if (undressType.isReuseMode) {
      return openPortal(PORTALS.RESET_RE_USE_FACE);
    }

    if (collectionData) {
      return openPortal(PORTALS.USER_FLOW_COLLECTIONS);
    }

    if (clipData) {
      return openPortal(PORTALS.USER_FLOW_CLIPS);
    }

    openPortal(PORTALS.USER_FLOW_COSTUMES);
  }, [
    isInit,
    undressType.isPrompt,
    undressType.isReuseMode,
    collectionData,
    clipData,
    openPortal,
  ]);

  const onEditPose = useCallback(() => {
    if (!isInit) {
      return;
    }

    openPortal(PORTALS.USER_FLOW_POSES);
  }, [isInit, openPortal]);

  const onDeletePose = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isInit) {
        return;
      }

      updatePhotoSetting(clickFileId, "pose", initialSettings.pose);
    },
    [clickFileId, initialSettings.pose, isInit, updatePhotoSetting],
  );

  /* Ничего не отображаем, если:
   * - нет загруженного изображения
   * - генерация завершена успешно
   * - генерация завершена с ошибкой
   * - генерация завершена с блюром
   */
  if (
    isEndAllUndress ||
    isError ||
    isBlur ||
    (!clickUndressingFile && !reUseFaceObj) ||
    !cardData
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "grid w-full shrink-0 grid-rows-[1fr] overflow-hidden transition-padding-grid-rows",
        isShowDraw && "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="flex min-h-0 gap-2">
        <DetailsCard
          image={cardData.data?.image ?? ""}
          title={cardData.title}
          name={getLocalizedName(cardData.data, locale)}
          count={undressType.isCollection ? selectedSetCount : undefined}
          isInit={isInit}
          isReuse={undressType.isReuseMode}
          onClick={handleEditCard}
          onDelete={undressType.isReuseMode ? handleEditCard : undefined}
        />

        {/* Если выбрана Поза и "классический" режим (костюм+поза) */}
        {poseData && undressType.isClassicMode && (
          <DetailsCard
            image={poseData.image ?? ""}
            title={t("title.pose")}
            name={getLocalizedName(poseData.name, locale)}
            isInit={isInit}
            onClick={onEditPose}
            onDelete={onDeletePose}
          />
        )}

        {/* Если старт флоу, не выбрана поза и "классический" режим (костюм+поза) */}
        {isInit && !poseData && undressType.isClassicMode && (
          <div
            className={cn(
              STYLES.base,
              STYLES.pointer,
              "aspect-square h-full w-fit items-center justify-center",
            )}
            onClick={onEditPose}
          >
            <Icon icon="add" />
          </div>
        )}
      </div>

      <ResetReUseFacePortal />
    </div>
  );
};
